/*
 * Ryan O'Dowd
 * 2025-05-28
 * © Copyright 2025 Oakwood Software Consulting, Inc.  All Rights Reserved.
 */
import {
  FEEDBACK_CHAT_ROUTE,
  FRIENDS_ROUTE,
  fetchChatMessages,
  fetchFriends,
} from '@utils/store/actions';
import notifee, {
  EventType,
} from '@notifee/react-native';
import {
  useCallback,
  useEffect,
} from 'react';
import {
  channelIds,
} from '@utils/globals';
import fb from '@utils/firebase';
import useNavigation from './useNavigation';
import useTypedDispatch from './useTypedDispatch';

notifee.createChannel({
  id: channelIds.DEFAULT,
  name: 'Default Channel',
});
notifee.createChannel({
  id: channelIds.FEEDBACK,
  name: 'Feedback Chat',
  description: 'Notification of new message in feedback chat',
});
notifee.createChannel({
  id: channelIds.STREAK_REMINDER,
  name: 'Streak Reminders',
  description: 'Reminders to keep your streak going',
});
notifee.createChannel({
  id: channelIds.DAILY_GOALS_REMINDER,
  name: 'Daily Goals Reminders',
  description: 'Reminders to complete your daily goals',
});
notifee.createChannel({
  id: channelIds.FRIENDS,
  name: 'Friends Notifications',
  description: 'Notifications for new followers and friend activity',
});
notifee.createChannel({
  id: channelIds.DEADLINES,
  name: 'Deadline Reminders',
  description: 'Reminders for assignments with upcoming deadlines', // @TODO: wording
});

export default function useOnNotificationPress() {
  const dispatch = useTypedDispatch();
  const navigation = useNavigation();

  const _handleNotificationPress = useCallback(async (message) => {
    if (message.data.channel_id === channelIds.FEEDBACK) {
      dispatch(fetchChatMessages());
      navigation.navigate(FEEDBACK_CHAT_ROUTE);
    } else if (message.data.channel_id === channelIds.FRIENDS) {
      dispatch(fetchFriends());
      navigation.navigate(FRIENDS_ROUTE, { // @TODO: data field could have more than follower_id to send to profile
        openTab: 'followers',
      });
    }
    // @TODO: if streak, lesson, goal, or other reminder, take to quick lesson?
  }, [dispatch, navigation]);

  useEffect(() => {
    // handle foreground
    const foregroundSubscription = notifee.onForegroundEvent(async ({type, detail}) => {
      if (type === EventType.PRESS) {
        await _handleNotificationPress(detail.notification);
      }
    });

    // handle backgrounded
    const backgroundSubscription = fb.onNotificationOpenedApp(fb.messaging, (remoteMessage) => _handleNotificationPress(remoteMessage));

    // handle killed
    fb.getInitialNotification(fb.messaging).then((remoteMessage) => {
      if (remoteMessage) {
        _handleNotificationPress(remoteMessage);
      }
    });

    return () => {
      foregroundSubscription();
      backgroundSubscription();
    };
  }, [dispatch, _handleNotificationPress]);
}
